import React, { useEffect } from 'react';
import { toastr } from 'react-redux-toastr';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/free-solid-svg-icons';

import { formatPlate } from '../garage/format';

// --- Formatacao (dados do painel) ---

const MONTHS = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const toDate = (value) => {
  const date = value ? new Date(value) : null;
  return date && !Number.isNaN(date.getTime()) ? date : null;
};

// "18 set 2026"
export const formatDay = (value) => {
  const date = toDate(value);
  return date ? `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}` : '—';
};

// "18 set 2026, 14:32"
export const formatDateTime = (value) => {
  const date = toDate(value);
  if (!date) return '—';
  return `${formatDay(date)}, ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
};

// "há 3 semanas", "hoje", "ontem"
export const formatRelative = (value, now = Date.now()) => {
  const date = toDate(value);
  if (!date) return '';
  const days = Math.floor((now - date.getTime()) / 86400000);
  if (days <= 0) return 'hoje';
  if (days === 1) return 'ontem';
  if (days < 7) return `há ${days} dias`;
  if (days < 30) return `há ${Math.floor(days / 7)} ${Math.floor(days / 7) === 1 ? 'semana' : 'semanas'}`;
  if (days < 365) return `há ${Math.floor(days / 30)} ${Math.floor(days / 30) === 1 ? 'mês' : 'meses'}`;
  const years = Math.floor(days / 365);
  return `há ${years} ${years === 1 ? 'ano' : 'anos'}`;
};

const digits = (value) => String(value || '').replace(/\D/g, '');

export const formatPhoneBR = (value) => {
  const raw = String(value || '');
  const d = digits(raw);
  // Ja mascarado pela API ("(19) •••••-8888"): mantem.
  if (raw.includes('•')) return raw;
  if (d.length === 11) return d.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
  if (d.length === 10) return d.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  return raw || '—';
};

export const formatDocument = (value) => {
  const raw = String(value || '');
  if (raw.includes('•')) return raw;
  const d = digits(raw);
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
  return raw || '—';
};

export const documentLabel = (value) => (digits(value).length === 14 || String(value || '').includes('/') ? 'CNPJ' : 'CPF');

const money = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });
export const formatMoneyBR = (value) => (value === null || value === undefined ? '—' : money.format(value));

const integer = new Intl.NumberFormat('pt-BR');
export const formatKmBR = (km) => (km ? `${integer.format(km)} km` : null);

/** "/" foca a busca (como Linear, Stripe, GitHub), fora de campos de texto. */
export function useSlashToSearch(inputId) {
  useEffect(() => {
    const onKey = (event) => {
      if (event.key !== '/' || event.ctrlKey || event.metaKey || event.altKey) return;
      const tag = (event.target && event.target.tagName) || '';
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(tag) || (event.target && event.target.isContentEditable)) return;
      const input = document.getElementById(inputId);
      if (input) {
        event.preventDefault();
        input.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [inputId]);
}

/** Atalho visivel ao lado da busca. */
export const ShortcutHint = () => <kbd className='ad-kbd' aria-hidden='true'>/</kbd>;

// --- Componentes ---

const PALETTE = ['#ff5a1f', '#111318', '#0f8a7e', '#7c3aed', '#c2410c', '#2563eb', '#be185d', '#4d7c0f'];

const hashOf = (text) => [...String(text || '')].reduce((sum, ch) => (sum * 31 + ch.charCodeAt(0)) % 9973, 7);

export const initialsOf = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return `${parts[0].charAt(0)}${parts.length > 1 ? parts[parts.length - 1].charAt(0) : ''}`.toUpperCase();
};

/** Iniciais com cor estavel por pessoa (a mesma em todo o painel). */
export function Avatar({ name, size = 'md', square = false }) {
  const color = PALETTE[hashOf(name) % PALETTE.length];
  return (
    <span className={`ad-avatar ad-avatar-${size}${square ? ' is-square' : ''}`} style={{ '--ad-avatar': color }} aria-hidden='true'>
      {initialsOf(name)}
    </span>
  );
}

/** Data em bloco: dia grande, mes e ano embaixo. */
export function DateBadge({ value }) {
  const date = toDate(value);
  if (!date) return <span className='ad-datebadge is-empty' aria-hidden='true'>—</span>;
  return (
    <span className='ad-datebadge' aria-hidden='true'>
      <strong>{String(date.getDate()).padStart(2, '0')}</strong>
      <span>{MONTHS[date.getMonth()]} {String(date.getFullYear()).slice(2)}</span>
    </span>
  );
}

/** Placa em texto, no formato oficial (regra clean: sem imagem de placa). */
export function PlateTag({ value }) {
  return <span className='ad-plate'>{formatPlate(value)}</span>;
}

export async function copyText(text, label) {
  try {
    await navigator.clipboard.writeText(String(text));
    toastr.success(`${label} copiado.`);
  } catch (err) {
    toastr.warning('Não foi possível copiar.');
  }
}

/** Linha de dado: icone, rotulo e valor; com `copy`, um botao para copiar. */
export function Fact({ icon, label, value, copy }) {
  const empty = value === null || value === undefined || value === '' || value === '—';
  return (
    <div className='ad-fact'>
      <span className='ad-fact-icon' aria-hidden='true'><FontAwesomeIcon icon={icon} /></span>
      <span className='ad-fact-text'>
        <span className='ad-fact-label'>{label}</span>
        <span className={`ad-fact-value${empty ? ' is-empty' : ''}`}>{empty ? 'Não informado' : value}</span>
      </span>
      {copy && !empty && (
        <button type='button' className='ad-copy' onClick={() => copyText(copy, label)} aria-label={`Copiar ${label}`} title='Copiar'>
          <FontAwesomeIcon icon={faCopy} />
        </button>
      )}
    </div>
  );
}
